import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Edit2, Save, X, AlertCircle, Users, Award, Building2, Check } from 'lucide-react'
import api from '../../services/api'

const TL_COLOUR = { green: 'var(--green)', amber: 'var(--amber)', red: 'var(--red)' }
const TL_LABEL = { green: 'Compliant', amber: 'Action Required', red: 'Non-Compliant' }
const TL_BG = { green: '#f0fdf4', amber: '#fffbeb', red: '#fef2f2' }

const EXPIRY_COLOUR: Record<string, string> = {
  expired: 'var(--red)', expiring_soon: 'var(--amber)', valid: 'var(--green)', no_expiry: 'var(--text-muted)',
}
const EXPIRY_BADGE: Record<string, string> = {
  expired: 'badge-critical', expiring_soon: 'badge-high', valid: 'badge-low', no_expiry: 'badge-medium',
}

const WORK_ACTIVITIES = [
  'Electrical', 'Plumbing', 'Civil / Earthworks', 'Roofing', 'Scaffolding',
  'Demolition', 'Asbestos Removal', 'Confined Space', 'Working at Heights',
  'Rigging / Crane', 'Traffic Management', 'Landscaping', 'Cleaning',
  'Security', 'IT / Communications', 'Mechanical', 'HVAC', 'Other',
]

type Tab = 'overview' | 'personnel' | 'competencies'

function ComplianceCard({ label, value, expiry, ok }: { label: string; value?: string; expiry?: string | null; ok?: boolean }) {
  const today = new Date()
  const in30 = new Date(today.getTime() + 30 * 86400000)
  let status: 'ok' | 'warn' | 'bad' = 'ok'
  let expiryLabel = '—'

  if (expiry) {
    const d = new Date(expiry)
    expiryLabel = d.toLocaleDateString('en-AU')
    if (d < today) status = 'bad'
    else if (d < in30) status = 'warn'
  } else if (ok === false) {
    status = 'bad'
  } else if (ok === undefined && !expiry) {
    status = 'warn'
  }

  const colour = status === 'bad' ? 'var(--red)' : status === 'warn' ? 'var(--amber)' : 'var(--green)'
  const bg = status === 'bad' ? '#fef2f2' : status === 'warn' ? '#fffbeb' : '#f0fdf4'

  return (
    <div className="rounded-xl p-4 border" style={{ borderColor: colour, background: bg }}>
      <div className="text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>{label}</div>
      {expiry !== undefined ? (
        <div className="font-semibold text-sm" style={{ color: colour }}>{expiryLabel}</div>
      ) : (
        <div className="font-semibold text-sm" style={{ color: colour }}>{ok ? 'Current' : 'Overdue'}</div>
      )}
      {value && <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{value}</div>}
    </div>
  )
}

export default function ContractorDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<Tab>('overview')
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<any>({})
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState('')

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3000) }

  const load = async () => {
    setLoading(true)
    try {
      const { data: res } = await api.get(`/contractors/${id}`)
      setData(res)
      setForm({
        company_name: res.company_name,
        abn: res.abn || '',
        primary_contact: res.primary_contact || '',
        email: res.email || '',
        mobile: res.mobile || '',
        licence_status: res.licence_status || 'active',
        licence_expires_at: res.licence_expires_at || '',
        insurance_public_liability_expiry: res.insurance_public_liability_expiry || '',
        insurance_wc_expiry: res.insurance_wc_expiry || '',
        induction_current: !!res.induction_current,
        prequalification_score: res.prequalification_score ?? '',
        activities: JSON.parse(res.activities || '[]'),
        last_audit_date: res.last_audit_date || '',
        last_audit_score: res.last_audit_score ?? '',
      })
    } catch {}
    setLoading(false)
  }

  useEffect(() => { load() }, [id])

  const handleSave = async () => {
    setSaving(true)
    try {
      await api.patch(`/contractors/${id}`, {
        ...form,
        prequalification_score: form.prequalification_score !== '' ? parseFloat(form.prequalification_score) : null,
        last_audit_score: form.last_audit_score !== '' ? parseFloat(form.last_audit_score) : null,
        induction_current: form.induction_current ? 1 : 0,
      })
      setEditing(false)
      showToast('Saved')
      load()
    } catch {}
    setSaving(false)
  }

  const toggleActivity = (act: string) => {
    setForm((f: any) => ({
      ...f,
      activities: f.activities.includes(act) ? f.activities.filter((a: string) => a !== act) : [...f.activities, act],
    }))
  }

  if (loading) return <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>Loading...</div>
  if (!data) return <div className="text-center py-12" style={{ color: 'var(--red)' }}>Contractor not found</div>

  const tl = data.traffic_light as 'green' | 'amber' | 'red'
  const activities: string[] = JSON.parse(data.activities || '[]')

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'personnel', label: 'Personnel', count: data.workers?.length || 0 },
    { id: 'competencies', label: 'Competencies', count: data.competencies?.length || 0 },
  ]

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button onClick={() => navigate('/contractors')} className="btn btn-secondary mt-1" style={{ padding: '6px 10px' }}>
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{data.company_name}</h1>
            <span
              className="flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium"
              style={{ background: TL_BG[tl], color: TL_COLOUR[tl], border: `1px solid ${TL_COLOUR[tl]}` }}
            >
              <span className="w-2 h-2 rounded-full" style={{ background: TL_COLOUR[tl] }} />
              {TL_LABEL[tl]}
            </span>
          </div>
          <div className="flex flex-wrap gap-4 mt-1 text-sm" style={{ color: 'var(--text-muted)' }}>
            {data.abn && <span>ABN {data.abn}</span>}
            {data.primary_contact && <span>{data.primary_contact}</span>}
            {data.email && <span>{data.email}</span>}
            {data.mobile && <span>{data.mobile}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <button onClick={handleSave} className="btn btn-primary" disabled={saving}><Save size={15} />{saving ? 'Saving...' : 'Save'}</button>
              <button onClick={() => setEditing(false)} className="btn btn-secondary"><X size={15} /></button>
            </>
          ) : (
            <button onClick={() => setEditing(true)} className="btn btn-secondary"><Edit2 size={15} /> Edit</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b" style={{ borderColor: 'var(--border)' }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-all"
            style={{
              borderBottom: `2px solid ${tab === t.id ? 'var(--accent)' : 'transparent'}`,
              color: tab === t.id ? 'var(--accent)' : 'var(--text-secondary)',
              background: 'none', border: 'none',
              borderBottomStyle: 'solid',
              borderBottomWidth: 2,
              borderBottomColor: tab === t.id ? 'var(--accent)' : 'transparent',
              cursor: 'pointer',
            }}
          >
            {t.label}
            {t.count !== undefined && (
              <span className="px-1.5 py-0.5 rounded text-xs" style={{ background: 'var(--bg)', color: 'var(--text-secondary)' }}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Overview tab */}
      {tab === 'overview' && (
        <div className="space-y-5">
          {/* Compliance cards */}
          <div>
            <h2 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Compliance Status</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <ComplianceCard label="PL Insurance" expiry={data.insurance_public_liability_expiry} />
              <ComplianceCard label="Workers Comp" expiry={data.insurance_wc_expiry} />
              <ComplianceCard label="Licence Expiry" expiry={data.licence_expires_at} value={data.licence_status} />
              <ComplianceCard label="Site Induction" ok={!!data.induction_current} />
            </div>
          </div>

          {editing ? (
            <div className="card p-5 space-y-5">
              <h3 className="font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>Edit Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Company Name</label>
                  <input value={form.company_name} onChange={e => setForm((f: any) => ({ ...f, company_name: e.target.value }))} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>ABN</label>
                  <input value={form.abn} onChange={e => setForm((f: any) => ({ ...f, abn: e.target.value }))} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Primary Contact</label>
                  <input value={form.primary_contact} onChange={e => setForm((f: any) => ({ ...f, primary_contact: e.target.value }))} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Email</label>
                  <input type="email" value={form.email} onChange={e => setForm((f: any) => ({ ...f, email: e.target.value }))} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Mobile</label>
                  <input value={form.mobile} onChange={e => setForm((f: any) => ({ ...f, mobile: e.target.value }))} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Licence Status</label>
                  <select value={form.licence_status} onChange={e => setForm((f: any) => ({ ...f, licence_status: e.target.value }))}>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Licence Expiry</label>
                  <input type="date" value={form.licence_expires_at} onChange={e => setForm((f: any) => ({ ...f, licence_expires_at: e.target.value }))} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>PL Insurance Expiry</label>
                  <input type="date" value={form.insurance_public_liability_expiry} onChange={e => setForm((f: any) => ({ ...f, insurance_public_liability_expiry: e.target.value }))} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Workers Comp Expiry</label>
                  <input type="date" value={form.insurance_wc_expiry} onChange={e => setForm((f: any) => ({ ...f, insurance_wc_expiry: e.target.value }))} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Pre-qual Score</label>
                  <input type="number" min="0" max="100" value={form.prequalification_score} onChange={e => setForm((f: any) => ({ ...f, prequalification_score: e.target.value }))} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Last Audit Date</label>
                  <input type="date" value={form.last_audit_date} onChange={e => setForm((f: any) => ({ ...f, last_audit_date: e.target.value }))} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Last Audit Score</label>
                  <input type="number" min="0" max="100" value={form.last_audit_score} onChange={e => setForm((f: any) => ({ ...f, last_audit_score: e.target.value }))} />
                </div>
                <div className="col-span-2 flex items-center gap-3">
                  <input type="checkbox" id="induction-edit" checked={form.induction_current} onChange={e => setForm((f: any) => ({ ...f, induction_current: e.target.checked }))} style={{ width: 16, height: 16 }} />
                  <label htmlFor="induction-edit" className="text-sm" style={{ color: 'var(--text-secondary)' }}>Site induction current</label>
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--text-secondary)' }}>Work Activities</label>
                <div className="flex flex-wrap gap-2">
                  {WORK_ACTIVITIES.map(act => (
                    <button key={act} type="button" onClick={() => toggleActivity(act)}
                      className="px-3 py-1 rounded-lg text-xs font-medium border transition-all"
                      style={{
                        background: form.activities.includes(act) ? 'var(--accent)' : 'var(--bg)',
                        color: form.activities.includes(act) ? 'white' : 'var(--text-secondary)',
                        borderColor: form.activities.includes(act) ? 'var(--accent)' : 'var(--border)',
                        cursor: 'pointer',
                      }}
                    >{act}</button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {/* Info card */}
              <div className="card p-5 space-y-3">
                <h3 className="font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>Company Info</h3>
                {[
                  ['ABN', data.abn],
                  ['Primary Contact', data.primary_contact],
                  ['Email', data.email],
                  ['Mobile', data.mobile],
                  ['Licence Status', data.licence_status],
                  ['Pre-qual Score', data.prequalification_score != null ? `${data.prequalification_score}%` : null],
                  ['Last Audit', data.last_audit_date ? `${new Date(data.last_audit_date).toLocaleDateString('en-AU')}${data.last_audit_score != null ? ` — ${data.last_audit_score}%` : ''}` : null],
                ].map(([label, val]) => val ? (
                  <div key={label as string} className="flex justify-between text-sm">
                    <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                    <span className="font-medium capitalize" style={{ color: 'var(--text)' }}>{val}</span>
                  </div>
                ) : null)}
              </div>

              {/* Activities card */}
              <div className="card p-5">
                <h3 className="font-semibold text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>Work Activities</h3>
                {activities.length === 0 ? (
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No activities specified</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {activities.map(a => (
                      <span key={a} className="px-2 py-1 rounded text-xs font-medium" style={{ background: 'var(--bg)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>{a}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Personnel tab */}
      {tab === 'personnel' && (
        <div className="card p-0 overflow-hidden">
          {data.workers?.length === 0 ? (
            <div className="text-center py-12">
              <Users size={36} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
              <p style={{ color: 'var(--text-muted)' }}>No workers linked to this contractor</p>
              <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Link workforce records via the Workforce module</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Name</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Job Title</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Department</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Site</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Type</th>
                </tr>
              </thead>
              <tbody>
                {data.workers.map((w: any) => (
                  <tr key={w.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td className="px-4 py-3 font-medium" style={{ color: 'var(--text)' }}>{w.first_name} {w.last_name}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{w.job_title || '—'}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{w.department || '—'}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{w.site_name || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="badge badge-medium capitalize">{w.employment_type?.replace('_', ' ') || '—'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Competencies tab */}
      {tab === 'competencies' && (
        <div className="card p-0 overflow-hidden">
          {data.competencies?.length === 0 ? (
            <div className="text-center py-12">
              <Award size={36} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
              <p style={{ color: 'var(--text-muted)' }}>No competency records for this contractor's workers</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Worker</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Licence / Competency</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Number</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Expiry</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.competencies.map((c: any) => (
                  <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td className="px-4 py-3 font-medium" style={{ color: 'var(--text)' }}>{c.worker_name}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--text)' }}>{c.competency_name}</td>
                    <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--text-secondary)' }}>{c.licence_number || '—'}</td>
                    <td className="px-4 py-3 text-xs font-medium" style={{ color: EXPIRY_COLOUR[c.expiry_status] }}>
                      {c.expiry_date ? new Date(c.expiry_date).toLocaleDateString('en-AU') : 'No expiry'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`badge ${EXPIRY_BADGE[c.expiry_status] || ''} capitalize`}>
                        {c.expiry_status?.replace('_', ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-white" style={{ background: 'var(--green, #16a34a)', zIndex: 9999 }}>
          <Check size={16} />{toast}
        </div>
      )}
    </div>
  )
}
